/**
 * ItemRankingsSection — every ranking this item has received, and where each
 * one stands.
 *
 * An item has no rank of its own. It is ranked by each session that takes it
 * up, placed on the lists a track puts forward, and only when it becomes part
 * of a Plan of Record does it hold an official rank. This section shows all of
 * it in one place, most authoritative first, so nobody reads a session's
 * number as the answer.
 *
 * Self-contained (same pattern as ItemCommitmentsSection): owns its read.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout, EmptyState, SkeletonText } from '@/components/ds';
import { isEmailAddress, knownActorName, useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { formatDate } from '@/i18n/formatters';
import { fetchItemRankings, type ItemRankingsResult } from '@/lib/itemRankingsService';
import {
  rankStandingLabel,
  summarizeItemRankings,
  type ItemRanking,
  type RankStanding,
} from '@/lib/itemRankings';
import { decisionForRanking, rankingDecisionGapText } from '@/lib/call/rankingDecision';
import { fetchListApprovalDecisionIds } from '@/lib/call/rankingDecisionService';
import { useDecisionsStore } from '@/stores/prioritization/decisionsStore';
import type { Decision } from '@/types/prioritization/decisions';
import { CallSentence } from '../decisions/CallSentence';
import { RankMark } from '../RankMark';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const GROUPS: Array<{ title: string; note: string; standings: RankStanding[] }> = [
  {
    title: 'Of record',
    note: 'The rank this item holds in a Plan of Record. One per plan, shared with no other item.',
    standings: ['official'],
  },
  {
    title: 'Authorized, not in force yet',
    note: 'From a plan that is authorized but has not started. Of record from its start date.',
    standings: ['starts_later'],
  },
  {
    title: 'Priority lists',
    note: 'Where a track has placed it. Agreed or proposed, not of record until a plan is authorized.',
    standings: ['agreed', 'proposed'],
  },
  {
    title: 'Session ranks',
    note: 'What each session that ranked it came up with. A session rank can tie.',
    standings: ['session'],
  },
  {
    title: 'Replaced',
    note: 'Ranks of record from a Plan of Record that a newer one has replaced.',
    standings: ['replaced'],
  },
];

/**
 * The call behind one ranking, drawn under the rank it produced (owner,
 * 2026-10-30: a standing is never drawn alone). When the record holds no
 * decision for the rank, it says so in words; while the record is still being
 * read, it says that, never a false "none".
 */
function RankingCall({
  ranking,
  decisions,
  listDecisionIds,
}: {
  ranking: ItemRanking;
  decisions: readonly Decision[] | null;
  listDecisionIds: ReadonlyMap<string, string> | null;
}) {
  if (decisions === null || listDecisionIds === null) {
    return <p className="text-xs text-[var(--text-ter)]">Reading the decision record…</p>;
  }
  const { decision, gap } = decisionForRanking(ranking, decisions, listDecisionIds);
  if (decision) {
    return <CallSentence compact eyebrow="The call behind this rank" decision={decision} decisions={decisions} />;
  }
  return <p className="text-xs text-[var(--text-ter)]">{rankingDecisionGapText(ranking, gap ?? 'not_on_record')}</p>;
}

function RankingRow({
  ranking,
  nameFor,
  decisions,
  listDecisionIds,
}: {
  ranking: ItemRanking;
  nameFor: (who: string | null) => string | null;
  decisions: readonly Decision[] | null;
  listDecisionIds: ReadonlyMap<string, string> | null;
}) {
  const official = ranking.standing === 'official';
  const below = ranking.aboveTheLine === false;
  const date = ranking.at ? formatDate(ranking.at, { month: 'short', day: 'numeric', year: 'numeric' }) || null : null;
  const by = nameFor(ranking.by);

  const facts = [
    ranking.outOf != null ? `of ${ranking.outOf}` : null,
    below ? 'below the line' : null,
    ranking.rank == null ? 'not ranked' : null,
    official || ranking.standing === 'replaced' || ranking.standing === 'starts_later'
      ? [by ? `authorized by ${by}` : 'authorized', date].filter(Boolean).join(' ')
      : date,
  ].filter(Boolean);

  return (
    <li className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
      <div className="flex items-center gap-3">
      <RankMark
        rank={ranking.rank}
        official={official}
        tied={ranking.tied}
        aboveTheLine={ranking.aboveTheLine}
        replaced={ranking.standing === 'replaced'}
      />
      <div className="min-w-0 flex-1">
        {ranking.sourceHref ? (
          <Link
            to={ranking.sourceHref}
            className={`block truncate text-sm font-medium hover:underline ${below ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}
          >
            {ranking.sourceName}
          </Link>
        ) : (
          <span className={`block truncate text-sm font-medium ${below ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
            {ranking.sourceName}
          </span>
        )}
        <p className="truncate text-xs text-[var(--text-ter)]">{facts.join(' · ')}</p>
      </div>
      <span className="flex-shrink-0 text-xs text-[var(--text-ter)]">
        {ranking.tied ? 'Tied · ' : ''}
        {rankStandingLabel(ranking)}
      </span>
      </div>
      <div className="mt-2 border-t border-[var(--rule)] pt-2" data-testid="ranking-call">
        <RankingCall ranking={ranking} decisions={decisions} listDecisionIds={listDecisionIds} />
      </div>
    </li>
  );
}

export function ItemRankingsSection({ itemId }: { itemId: string }) {
  const [result, setResult] = useState<ItemRankingsResult | null>(null);

  // The decisions each rank's call is read from: the workspace's decision
  // record (the same rows the Decision Log shows), plus the one link the
  // decision rows do not carry, which approved list wrote which decision.
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const [decisionsReady, setDecisionsReady] = useState(decisions.length > 0);
  const [listDecisionIds, setListDecisionIds] = useState<ReadonlyMap<string, string> | null>(null);

  useEffect(() => {
    if (decisions.length > 0) {
      setDecisionsReady(true);
      return;
    }
    let cancelled = false;
    void fetchDecisions().finally(() => {
      if (!cancelled) setDecisionsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [decisions.length, fetchDecisions]);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    fetchItemRankings(itemId).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const rankings = useMemo(() => result?.rankings ?? [], [result]);

  useEffect(() => {
    const setIds = rankings
      .filter((r) => r.standing === 'agreed' && r.prioritySetId)
      .map((r) => r.prioritySetId as string);
    if (setIds.length === 0) {
      setListDecisionIds(new Map());
      return;
    }
    let cancelled = false;
    setListDecisionIds(null);
    fetchListApprovalDecisionIds(setIds)
      .then((m) => {
        if (!cancelled) setListDecisionIds(m);
      })
      .catch(() => {
        if (!cancelled) setListDecisionIds(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [rankings]);

  // An authorization made before names were recorded holds a user id, or an
  // email, where the person's name belongs.
  const actorIds = useMemo(
    () => rankings.map((r) => r.by).filter((v): v is string => !!v && (UUID_RE.test(v) || isEmailAddress(v))),
    [rankings],
  );
  const actorMap = useActorDisplayMap(actorIds);
  const nameFor = (who: string | null): string | null => {
    if (!who) return null;
    if (!UUID_RE.test(who) && !isEmailAddress(who)) return who;
    // Until the person's name is read, the line leaves the name out rather
    // than printing an id or an address.
    return knownActorName(actorMap, who);
  };

  if (!result) {
    return <SkeletonText lines={4} />;
  }

  const summary = summarizeItemRankings(rankings);
  const hasOfficial = summary.official.length > 0;
  const officialUnread = result.unread.includes('ranks of record');

  return (
    <div className="space-y-6">
      {result.unread.length > 0 && (
        <Callout tone="coral" title={`Could not read this item's ${result.unread.join(' or ')}.`}>
          What is listed below is incomplete. Reload the page to try again.
        </Callout>
      )}

      {!officialUnread && (
        <Callout tone={hasOfficial ? 'green' : 'neutral'} label={hasOfficial ? 'Of record' : 'Not of record yet'}>
          {hasOfficial ? (
            <>
              {summary.official.map((r, i) => (
                <span key={r.key}>
                  {i > 0 ? '; ' : ''}
                  {r.rank != null ? `#${r.rank}` : 'Not ranked'}
                  {r.aboveTheLine === false ? ' (below the line)' : ''} in {r.sourceName}
                </span>
              ))}
              .
            </>
          ) : rankings.length === 0 ? (
            'Nobody has ranked this item yet.'
          ) : (
            'This item has no rank of record. It gets one when a Plan of Record that includes it is authorized.'
          )}
          {summary.sessionRange && (
            <>
              {' '}
              {summary.sessionRange.count === 1
                ? `One session ranked it #${summary.sessionRange.low}.`
                : summary.sessionRange.low === summary.sessionRange.high
                  ? `${summary.sessionRange.count} sessions all ranked it #${summary.sessionRange.low}.`
                  : `${summary.sessionRange.count} sessions ranked it between #${summary.sessionRange.low} and #${summary.sessionRange.high}.`}
            </>
          )}
        </Callout>
      )}

      {rankings.length === 0 && result.unread.length === 0 ? (
        <EmptyState
          title="No rankings yet"
          description="When a session ranks this item, a track puts it on a list, or a plan is authorized with it, each one shows here with its standing."
        />
      ) : (
        GROUPS.map((group) => {
          const rows = rankings.filter((r) => group.standings.includes(r.standing));
          if (rows.length === 0) return null;
          return (
            <section key={group.title} aria-label={group.title}>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">
                {group.title} <span className="normal-case tracking-normal">({rows.length})</span>
              </h3>
              <p className="mb-2 text-xs text-[var(--text-ter)]">{group.note}</p>
              <ul className="space-y-1.5">
                {rows.map((r) => (
                  <RankingRow
                    key={r.key}
                    ranking={r}
                    nameFor={nameFor}
                    decisions={decisionsReady ? decisions : null}
                    listDecisionIds={listDecisionIds}
                  />
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
